import React, { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { UseMutationResult } from "react-query";

import { HookFormInputGroup, SpinnerLoader } from "components";
import { ErrorResponse } from "types";
import { maxLength50, required } from "validators";

type FeeTemplateNameFieldProps = {
	isLoading?: boolean;
	companyId: string;
	fieldInitialValue?: string | undefined;
	fieldData: {
		name: string;
		displayName: string;
	};
	useCheckFeeTemplateNameMutation: (
		onSuccess?: Function,
	) => UseMutationResult<any, ErrorResponse, any, unknown>;
};

const ERROR_MESSAGE = "Template Name is taken";

const FeeTemplateNameField = ({
	isLoading,
	companyId,
	fieldInitialValue = "",
	fieldData: { name: fieldName, displayName },
	useCheckFeeTemplateNameMutation,
}: FeeTemplateNameFieldProps) => {
	const [prevValue, setPrevValue] = useState<string>("");
	const [isNameUsed, setIssNameUsed] = useState<boolean>(false);

	const {
		register,
		trigger,
		setError,
		formState: { errors },
	} = useFormContext();

	const validator = useCallback(
		() => (isNameUsed ? ERROR_MESSAGE : undefined),
		[isNameUsed],
	);

	const {
		mutateAsync: checkFeeTemplateName,
		isLoading: isCheckingFeeTemplateName,
	} = useCheckFeeTemplateNameMutation();

	const handleChange = useCallback(e => {
		setIssNameUsed(false);
	}, []);

	const handleBlur = useCallback(
		e => {
			const value = e.target.value;

			if (value === prevValue) {
				return;
			}

			setPrevValue(value);

			trigger(fieldName).then(isValid => {
				if (!isValid || value === fieldInitialValue) {
					return;
				}

				checkFeeTemplateName({ companyId, templateName: value })
					.then(isInUse => {
						if (isInUse) {
							setError(fieldName, {
								type: "emailValid",
								message: ERROR_MESSAGE,
							});

							setIssNameUsed(true);
						}
					})
					.catch(() => {});
			});
		},
		[
			checkFeeTemplateName,
			companyId,
			fieldInitialValue,
			fieldName,
			prevValue,
			setError,
			trigger,
		],
	);

	return (
		<HookFormInputGroup
			name={fieldName}
			label={displayName}
			register={register}
			validator={{
				required,
				validator,
				maxLength50,
			}}
			onBlur={handleBlur}
			onChange={handleChange}
			blurLoading={isLoading}
			loading={isCheckingFeeTemplateName}
			loader={SpinnerLoader}
			errors={errors}
			showRequiredMark
		/>
	);
};

export default FeeTemplateNameField;
